import React from "react";
import { PacmanLoader } from "react-spinners";
import { Button, Col, Row } from "react-bootstrap";
import { Link } from "react-router-dom";
import { createColumnHelper } from "@tanstack/react-table";
import { GenericTable } from "components/GenericTable";
import EditableCell from "containers/common/EditableCell";
import "containers/common/List.css";
import "containers/common/Table.css";

function StrataUnitList({
  data,
  isLoading,
  isAuthenticated = true,
  controlType,
  saveRecord,
  deleteRecord,
  updateTableState,
  validateRelatedRecord,
}) {
  const dataSet = "strataUnits";
  const tableData = data || [];
  const columnHelper = createColumnHelper();

  const tableMeta = {
    updateData: (rowIndex, columnId, value) => {
      updateTableState(dataSet, rowIndex, columnId, value);
    },
    handleSave: async ({ row, column, value }) => {
      const record = {
        ...(tableData[row.index] || {}),
        ...row.original,
        ...(column?.id ? { [column.id]: value } : {}),
      };

      const validation = validateRelatedRecord?.(record, dataSet) || [true, null];
      if (validation[0] !== true) {
        alert(validation[1] || "Validation failed");
        return { validationFailed: true };
      }

      try {
        await saveRecord(dataSet, record);
      } catch (error) {
        console.error("Strata unit save failed:", error);
        return { saveFailed: true };
      }

      return true;
    },
    handleDelete: async ({ row }) => {
      await deleteRecord(dataSet, row.original);
    },
  };

  const columns = [
    columnHelper.accessor("selected", {
      id: "selected",
      header: "",
      cell: EditableCell,
      size: 60,
      style: { textAlign: "center" }
    }),
    columnHelper.accessor("property_id", {
      id: "property_id",
      header: "ID",
      cell: (e) => {
        const rowData = e?.row?.original || {};
        const rowPropertyKey = rowData.property_key;
        const selectedKeys = (tableData || [])
          .filter((a) => a?.selected)
          .map((a) => a?.property_key)
          .filter(Boolean);

        if (!rowPropertyKey) {
          return e.getValue();
        }

        return (
          <Link
            to={{
              pathname: `/properties/${rowPropertyKey}`,
              state: { tab: "property", data: selectedKeys },
            }}
          >
            {e.getValue()}
          </Link>
        );
      },
      size: 30,
      style: { backgroundColor: '#fafafa' }
    }),
    columnHelper.accessor("property_key", {
      id: "property_key",
      header: "Cityscope Reference",
      size: 50,
      style: { backgroundColor: '#fafafa' }
    }),
    columnHelper.accessor("unit_sort", {
      id: "unit_sort",
      header: "Unit Sort",
      cell: EditableCell,
      size: 30,
      style: { textAlign: "center", backgroundColor: '#fafafa' }
    }),
    columnHelper.accessor("unit_number", {
      id: "unit_number",
      header: "Unit Number",
      size: 30,
      style: { textAlign: "center", backgroundColor: '#fafafa' }
    }),
    columnHelper.accessor("unit_number_suffix", {
      id: "unit_number_suffix",
      header: "Unit Number Suffix",
      size: 30,
      style: { backgroundColor: '#fafafa' },
    }),
    columnHelper.accessor("description", {
      id: "description",
      header: "Description",
      size: 150,
      style: { textAlign: "left", backgroundColor: '#fafafa' },
      meta: { wrap: true },
    }),
    columnHelper.accessor("title_details", {
      id: "title_details",
      header: "Title",
      size: 85,
      style: { textAlign: "left", backgroundColor: '#fafafa' },
      meta: { wrap: true },
    }),
    columnHelper.accessor("address_count", {
      id: "address_count",
      header: "Address Count",
      size: 30,
      style: { textAlign: "center", backgroundColor: '#fafafa' }
    }),
    columnHelper.accessor("building_name_count", {
      id: "building_name_count",
      header: "Building Name Count",
      size: 30,
      style: { textAlign: "center", backgroundColor: '#fafafa' }
    }),
    columnHelper.accessor("classification_count", {
      id: "classification_count",
      header: "Classification Count",
      size: 30,
      style: { textAlign: "center", backgroundColor: '#fafafa' }
    }),
    columnHelper.accessor("owner_count", {
      id: "owner_count",
      header: "Owner Count",
      size: 30,
      style: { textAlign: "center", backgroundColor: '#fafafa' }
    }),
  ];

  function selectAll() {
    const localData = [...tableData];
    for (var i = 0; i < localData.length; i++) {
      localData[i].selected = true;
    }
    updateTableState(localData, dataSet);
  }

  function deselectAll() {
    const localData = [...tableData];
    for (var i = 0; i < localData.length; i++) {
      localData[i].selected = false;
    }
    updateTableState(localData, dataSet);
  }

  function sortUnits() {
    const localData = [...tableData];
    localData.sort(function (a, b) { return a.unit_sort - b.unit_sort });
    updateTableState(localData, dataSet);
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="StrataUnitList">
      {controlType === "table" && (
        <Row className="show-Container">
          <Col className="text-left">
            <Button variant="primary" type="button" size="sm" onClick={sortUnits}>
              Sort Units
            </Button>{" "}
            <Button variant="primary" type="button" size="sm" onClick={selectAll}>
              Select All
            </Button>{" "}
            <Button variant="primary" type="button" size="sm" onClick={deselectAll}>
              Deselect All
            </Button>{" "}
          </Col>
          <Col className="text-right" />
        </Row>
      )}

      <Row className="show-Container">
        <Col sm={12} md={12} lg={12}>
          {isLoading ? (
            <div className="outer-div">
              <div className="sweet-loading inner-div">
                <PacmanLoader
                  cssOverride={{ display: "block", margin: "0 auto" }}
                  size={20}
                  color={"#e03c31"}
                  loading={isLoading}
                />
              </div>
            </div>
          ) : (
            <GenericTable
              columns={columns}
              data={tableData}
              pageSize={10}
              enablePagination={true}
              hasHeaderSorting={true}
              meta={tableMeta}
            />
          )}
        </Col>
      </Row>
    </div>
  );
}

export default StrataUnitList;